<script>
import {defineComponent} from 'vue'
import {useDeliveryStore} from '@js/store/DeliveryStore.js'

import MobileTopBar from "@js/components/TopBar/MobileTopBar.vue";
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue";
import IconPaymentMaestro from "@js/components/Icons/IconPaymentMaestro.vue";
import IconPaymentVisa from "@js/components/Icons/IconPaymentVisa.vue";
import IconPaymentMir from "@js/components/Icons/IconPaymentMir.vue";
import IconPaymentMasterCard from "@js/components/Icons/IconPaymentMasterCard.vue";
import IconPaymentSbp from "@js/components/Icons/IconPaymentSbp.vue";

import DeliveryZonesMap from "@js/components/Sections/Delivery/DeliveryZonesMap.vue";
import DeliveryCalculatorPanel from "@js/components/Sections/Delivery/DeliveryCalculatorPanel.vue";
import {checkDocument} from "@js/helpers.ts";

export default defineComponent({
    name: "DeliveryAndShippingPage",
    components: {
        DeliveryCalculatorPanel,
        DeliveryZonesMap,
        IconPaymentSbp,
        IconPaymentMasterCard,
        IconPaymentMir,
        IconPaymentVisa,
        IconPaymentMaestro,
        Breadcrumbs,
        MobileTopBar,
    },

    setup() {
        const delivery = useDeliveryStore()
        if (checkDocument()) {
            // дефолт: манипулятор 6 м, Зона-1
            delivery.setVehicle('6m')
            delivery.setZone({id: 1, name: 'Зона 1', coeff: 1.00})
        }
        return {delivery}
    },

    data() {
        return {
            canLoadMap: false,
            selectedZone: {id: 1, name: 'Зона 1', coeff: 1.00},
            selectedPoint: null,
        };
    },

    methods: {
        onZoneChanged(z) {
            this.selectedZone = z;
            this.delivery.setZone(z);
        },
        onPointSelected(p) {
            this.selectedPoint = p;
            this.delivery.setCoords(p.coords);
        },
    },

    mounted() {
        if (checkDocument()) {
            this.canLoadMap = true
        }
    }
})
</script>

<template>
    <MobileTopBar/>
    <div class="container">
        <Breadcrumbs/>

        <h1 class="text-2xl md:text-3xl font-semibold mb-6">
            Расчёт доставки ЖБИ изделий
        </h1>

        <!-- Карта + калькулятор -->
        <section class="relative grid grid-cols-1 md:grid-cols-2 gap-5 mb-5 md:mb-10">
            <DeliveryZonesMap
                class="md:sticky md:top-24"
                v-if="canLoadMap"
                :zoom="8"
                :center="[55.462540, 37.781900]"
                @zone-changed="onZoneChanged"
                @point-selected="onPointSelected"
            />

            <!-- правая панель расчёта -->
            <div class="flex flex-col gap-5">
                <div class="bg-white rounded-3xl p-5 md:p-6 shadow-sm border">
                    <DeliveryCalculatorPanel v-if="canLoadMap"/>
                </div>

                <!-- Правила оказания услуги доставки (ЖБИ) -->
                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Правила доставки ЖБИ кран-манипулятором</h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>Доставляем железобетонные изделия (плиты перекрытия, дорожные плиты, ФБС, бордюрный камень и др.) кран-манипулятором <strong>6&nbsp;м</strong> или <strong>12&nbsp;м</strong> с разгрузкой на площадке.</li>
                        <li>Стандартная услуга <strong>не включает</strong> подъем в здания, пронос по территории или сборку — эти работы считаются дополнительными.</li>
                        <li>Разгрузка выполняется за <strong>монтажные петли</strong> или сертифицированные строповочные точки. Подготовьте ровную и безопасную площадку для складирования изделий.</li>
                        <li>Просим быть на связи и присутствовать в выбранный интервал: водитель звонит за <strong>10–15&nbsp;минут</strong> до прибытия на адрес.</li>
                        <li>При ограниченном подъезде разгружаем в максимально близкой доступной точке без нарушения ПДД и рисков для техники.</li>
                        <li>Платная парковка, оформление пропусков/въездов и иные локальные сборы <strong>оплачиваются заказчиком</strong>.</li>
                    </ul>
                    <p class="mt-4 text-sm text-gray-600">Сведения носят справочный характер и не являются публичной офертой. Уточняйте детали у менеджера при подтверждении заказа.</p>
                </div>

                <!-- Стоимость доставки: из чего складывается -->
                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Стоимость доставки ЖБИ: из чего складывается</h2>
                    <p class="mb-4">
                        Итоговая цена зависит от выбранной конфигурации кран-манипулятора (<strong>6&nbsp;м</strong> или <strong>12&nbsp;м</strong>), удалённости адреса (зона доставки), массы и габаритов изделий, количества точек разгрузки и выбранного режима — «в течение дня» или «точно ко времени».
                    </p>
                    <p class="mb-2">
                        Базовая ставка для <strong>Зоны&nbsp;1</strong> умножается на коэффициент зоны и на коэффициент выбранного режима. Рассчитайте стоимость в калькуляторе на этой странице — сумма обновляется автоматически при выборе адреса и конфигурации манипулятора.
                    </p>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>Разгрузка, подъем и перемещение изделий на объекте <strong>рассчитываются отдельно</strong>.</li>
                        <li>Для крупных партий ЖБИ и регулярных поставок действуют индивидуальные условия — сообщите менеджеру спецификацию (наименование, количество, масса единицы).</li>
                    </ul>
                </div>

                <!-- Ограничения и требования по ЖБИ -->
                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Ограничения и требования при разгрузке ЖБИ</h2>
                    <p class="mb-4">
                        Для безопасности работ учитывайте длину изделий, массу единицы и зону досягаемости стрелы. Ниже — типовые конфигурации:
                    </p>
                    <div class="grid grid-cols-2 gap-3 text-sm">
                        <div class="bg-white rounded-xl p-3 border">
                            <div class="font-medium">Манипулятор 6&nbsp;м</div>
                            <div class="text-gray-600">длина изделия до&nbsp;6&nbsp;м; разгрузка за монтажные петли/стропы</div>
                        </div>
                        <div class="bg-white rounded-xl p-3 border">
                            <div class="font-medium">Манипулятор 12&nbsp;м</div>
                            <div class="text-gray-600">длина изделия до&nbsp;12&nbsp;м; требуется подготовленная площадка</div>
                        </div>
                    </div>
                    <ul class="list-disc list-outside ps-5 space-y-2 mt-3">
                        <li>Масса единицы и вылет стрелы ограничены паспортом техники; при превышении параметров потребуется альтернативное решение (например, автокран).</li>
                        <li>Разгрузка <strong>запрещена</strong> под ЛЭП, над кровлей и в местах с риском для людей/имущества. Нужна свободная зона и прямая видимость точки складирования.</li>
                        <li>Для плит и ФБС обеспечьте доступ к монтажным петлям; не допускайте повреждения закладных элементов и кромок изделий.</li>
                    </ul>
                </div>

                <!-- Документы и получение -->
                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Документы и порядок получения ЖБИ</h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>Передаём комплект документов: товарно-транспортная накладная, счёт/УПД; по запросу — паспорт качества на ЖБИ.</li>
                        <li><strong>Юридическим лицам</strong> при безналичной оплате может потребоваться доверенность/печать — уточните заранее.</li>
                        <li>При расчёте на адресе кассовый чек направим на e-mail, указанный в заказе, либо выдадим в офисе/при следующей поставке.</li>
                        <li>Просьба проверить номенклатуру и целостность изделий до начала разгрузки и подписания документов.</li>
                    </ul>
                </div>

                <!-- Временные интервалы -->
                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Временные интервалы доставки</h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>Доставляем «в течение дня» либо <strong>точно ко времени</strong>; доступны интервалы вида 10–18 / 12–20 / 14–22.</li>
                        <li>Время разгрузки и подготовительных работ не входит в интервал доставки.</li>
                        <li>При нескольких рейсах на один адрес возможен сдвиг интервала ~на 1&nbsp;час.</li>
                        <li>Для удалённых зон время согласовывается индивидуально.</li>
                    </ul>
                </div>

                <!-- Простой и парковка -->
                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Простой техники и парковка</h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>В базовую ставку включено до <strong>60 минут</strong> простоя манипулятора на адресе.</li>
                        <li>Сверх включённого времени простой оплачивается по действующему тарифу.</li>
                        <li>Платная парковка/пропуска и иные локальные сборы оплачиваются заказчиком.</li>
                    </ul>
                    <p class="mt-4 text-sm text-gray-600">Фактические суммы зависят от маршрута, времени работ и условий на площадке; уточняйте у менеджера при подтверждении заказа.</p>
                </div>

                <!-- Разгрузка кран-манипулятором -->
                <div class="bg-grayBg rounded-3xl px-5 md:px-8 py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Разгрузка ЖБИ кран-манипулятором</h2>
                    <ul class="list-disc list-outside ps-5 space-y-2">
                        <li>Разгружаем изделия в зоне досягаемости стрелы при прямой видимости точки складирования; при необходимости — перестановка автомобиля.</li>
                        <li>Строповка выполняется только за петли/закладные элементы с использованием исправных строп и траверс.</li>
                        <li>Подземные паркинги и низкие проёмы обслуживаются по согласованию, с учётом габаритов техники и высоты подъезда.</li>
                        <li>Работы под ЛЭП или вблизи опасных зон не выполняются без дополнительных мер безопасности и согласований.</li>
                    </ul>
                    <p class="mt-4 text-sm text-gray-600">Опишите условия площадки (проезд, высота, ограничения, расстояние до места складирования) — это поможет точно рассчитать ставку и время работ.</p>
                </div>

            </div>
        </section>
    </div>
</template>

<style scoped>
/* при необходимости — доп. стили */
</style>
